import { DatePicker } from 'antd'
import { DatePickerProps } from 'antd/es/date-picker'
import dayjs, { Dayjs } from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { ComponentController } from './component_controller'
import { DispatchEvent } from '../../types'
import { dispatcher } from '../pub_controller'
import { isSafari } from '../../utils'

export class DatePickerController extends ComponentController<DatePickerProps> {
  static targets = ['component', 'input']

  declare componentTarget: HTMLElement
  declare inputTarget: HTMLInputElement
  declare datetimeField: string
  declare fieldId: string
  declare timeZone: string

  get component() {
    return DatePicker
  }

  get initialProps() {
    dayjs.extend(utc)
    dayjs.extend(timezone)
    const props = this.propsFromData
    const url = new URL(window.location.href)
    this.datetimeField = props['datetime_field']
    this.fieldId = props.id

    const timeZone = props['time_zone'] || 'UTC'
    dayjs.tz.setDefault(timeZone)
    this.timeZone = timeZone

    props['showToday'] = false

    const date =
      props[this.datetimeField] ||
      url.searchParams.get('datetimeField') ||
      props['value']

    let initialValue
    if (date) {
      try {
        // Parse initial value as UTC, then convert to display timezone
        initialValue = dayjs.utc(date).tz(timeZone)
        if (!initialValue.isValid()) {
          console.warn('Invalid date detected:', date)
          initialValue = null
        }
      } catch (e) {
        console.warn('Error parsing date:', e, date)
        initialValue = null
      }
    }

    let maxDate
    if (props['maxDate']) {
      maxDate = dayjs(props['maxDate']).tz(timeZone)
    }

    return {
      ...props,
      maxDate: maxDate,
      onChange: this.onChange.bind(this),
      value: initialValue,
    }
  }

  get rootElement() {
    return this.componentTarget
  }

  update(event: DispatchEvent<{ content: string }>) {
    const value = this.getPropsFromEvent(event)['value']
    if (value != null) this.updateTargets(value)
    super.update(event)
  }

  onChange(date: any, dateString: string): void {
    this.updateTargets(date)
    this.updateProps({ value: date })

    const dateInput = document.getElementById(this.fieldId) as HTMLInputElement

    if (dateInput) {
      dateInput.value = dayjs(dateString).tz(this.timeZone).format('YYYY-MM-DD')
    }
  }

  reset() {
    this.updateProps({ value: null })
  }

  handleTimezoneChangeEvent(event: DispatchEvent<{ time_zone: string }>): void {
    const newTZ = event.detail.time_zone
    this.timeZone = newTZ
    dayjs.tz.setDefault(newTZ)

    const hiddenField = document.getElementById(
      this.datetimeField,
    ) as HTMLInputElement
    const utcValue = hiddenField.value
    const newDate = utcValue ? dayjs.utc(utcValue).tz(newTZ) : null

    this.updateProps({ value: newDate })

    this.updateDateTimeField(newDate)

    const dateInput = document.getElementById(this.fieldId) as HTMLInputElement
    if (dateInput) {
      dateInput.dispatchEvent(new Event('change'))
    }
  }

  private updateTargets(date: Dayjs | null): void {
    const field = document.getElementById(this.fieldId) as HTMLInputElement
    field.value = date ? date.format('YYYY-MM-DD') : ''
    field.dispatchEvent(new Event('change'))

    this.updateDateTimeField(date)

    const pubChannel = field.getAttribute('data-pub-channel-value')
    const pubAs = field.getAttribute('data-pub-as-value')

    if (pubChannel && pubAs) {
      dispatcher(this, `${pubAs}_text`, pubChannel, date)
    }
  }

  private updateDateTimeField(date: Dayjs | null): void {
    const field = document.getElementById(
      this.datetimeField,
    ) as HTMLInputElement

    if (date) {
      let currentValue = field.value

      if (isSafari()) {
        // because day js fails to parse dates with '-' in safari [SHEP-1643]
        currentValue = currentValue.replaceAll('-', '/')
      }

      let currentDateTime = null

      try {
        // Parse current value in the local timezone
        currentDateTime = currentValue
          ? dayjs(currentValue).tz(this.timeZone)
          : null

        if (currentDateTime && !currentDateTime.isValid()) {
          console.warn('Invalid current datetime detected:', currentValue)
          currentDateTime = null
        }
      } catch (e) {
        console.warn('Error parsing current datetime:', e, currentValue)
        currentDateTime = null
      }

      if (currentDateTime) {
        try {
          // Update the date components in the local timezone
          const newLocalDateTime = currentDateTime
            .year(date.year())
            .month(date.month())
            .date(date.date())

          // Convert to UTC for storage
          field.value = newLocalDateTime.utc().format('YYYY-MM-DD HH:mm:ss[Z]')
        } catch (e) {
          console.warn('Error updating datetime field:', e)
          // Fallback: create new datetime in local timezone then convert to UTC
          const now = dayjs().tz(this.timeZone)
          const newLocalDateTime = now
            .year(date.year())
            .month(date.month())
            .date(date.date())
          field.value = newLocalDateTime.utc().format('YYYY-MM-DD HH:mm:ss[Z]')
        }
      } else {
        // No current datetime, create new one in local timezone at start of day
        const newLocalDateTime = date.tz(this.timeZone).startOf('day')
        field.value = newLocalDateTime.utc().format('YYYY-MM-DD HH:mm:ss[Z]')
      }
    } else {
      field.value = ''
    }

    field.dispatchEvent(new Event('change'))
  }
}
